/**
 * Choosing WHICH occurrence of a cited figure to highlight (#11237).
 *
 * The highlighter used to take the first `indexOf` hit in document order. A
 * figure like "16.0%" — or worse a short one — routinely appears more than
 * once on a page (a header, a page number, an unrelated table), so the first
 * hit was frequently not the one the citation resolved to. That is the
 * "citations lead to random places in the document" report.
 *
 * The citation click already carries the evidence needed to disambiguate: the
 * harvested passage text (`highlightContext`), which is verbatim text from the
 * document rather than the model's paraphrase. Locating that passage on the
 * page gives a window, and the occurrence inside the window is the cited one.
 *
 * This is deliberately a PREFERENCE, not a filter. Every path that cannot find
 * a better answer returns the first occurrence, which is what shipped before —
 * so `chooseAnchoredOccurrence` can improve a highlight or leave it alone,
 * never worsen it. `chooseAnchoredHit` (#12090) adds one more preference
 * that is a trade rather than a guarantee; its docblock says which.
 *
 * This is the in-bundle copy, used by the viewers that render inside the app.
 * Its twin lives under `public/pdfjs-4.5.136-dist/web/addOnServices/` for the
 * PDF viewer, which is a static asset served into an iframe and so cannot
 * import from the app bundle. `anchorOccurrence.test.ts` runs this suite
 * against BOTH, the way the existing `splitIntoSentences` parity test does.
 *
 * The sentence segmenter is duplicated from `highlightTextNodes.js` rather
 * than imported: that module imports THIS one, and a cycle between two files
 * that both run at viewer boot is not worth saving four lines.
 */

const sentenceSegmenter = new Intl.Segmenter("en", { granularity: "sentence" });

const splitIntoSentences = (text) =>
  `${text}`
    .split("\n")
    .flatMap((line) =>
      [...sentenceSegmenter.segment(line)].map((s) => s.segment)
    )
    .map((s) => s.trim())
    .filter(Boolean);

// Shortest context sentence allowed to define the anchor window. Below this a
// sentence is too generic to localize anything, and a bogus window can make
// the highlight worse than no anchoring at all.
const MIN_ANCHOR_SENTENCE_CHARS = 24;

/** Every index at which `phrase` occurs in `text`. Both already normalized. */
const allOccurrences = (text, phrase) => {
  const out = [];
  if (!phrase) return out;
  let from = 0;
  for (;;) {
    const idx = text.indexOf(phrase, from);
    if (idx === -1) return out;
    out.push(idx);
    // Overlapping occurrences count: "5.5% to 5.5%" with a one-char phrase
    // must not skip a hit.
    from = idx + 1;
  }
};

/**
 * A cited passage as the page would print it (#12090). An extracted table
 * arrives as markdown — a `###` caption, `|`-fenced cells, `|-|-|` rules —
 * none of which is on the page, so no line of it could locate. Stripping
 * the syntax leaves the caption and the cell text, which can. Prose passes
 * through unchanged.
 */
const plainText = (context) =>
  `${context ?? ""}`
    .split("\n")
    .map((line) => line.replace(/^#+\s*/, "").replace(/\|/g, " "))
    .filter((line) => /[^\s-]/.test(line))
    .map((line) => line.replace(/\s+/g, " ").trim())
    .join("\n");

/**
 * `pageText` with every whitespace run (no-break spaces included) read as one
 * space, and, for each position of that text, the page position it came
 * from (#14039). `plainText` collapses the context the same way, but the
 * viewers join a page's text nodes as they are: a note's text layer writes
 * "Investure:\u00a0\u00a0\u00a0 ~$800 MM", which no collapsed context is a
 * substring of. The map keeps every span this returns in the page's own
 * offsets, which the hits it is compared with index.
 */
const collapsedPage = (pageText) => {
  const chars = [];
  const at = [];
  let inRun = false;
  for (let i = 0; i < pageText.length; i++) {
    const ch = pageText[i];
    if (/\s/.test(ch)) {
      if (inRun) continue;
      inRun = true;
      chars.push(" ");
    } else {
      inRun = false;
      chars.push(ch);
    }
    at.push(i);
  }
  return { text: chars.join(""), at };
};

/**
 * The `[start, end)` span of `phrase` in the page, in page offsets, or null.
 * The page as it is first (what shipped), then the page with its whitespace
 * runs collapsed.
 */
const findOnPage = (pageText, collapsed, phrase) => {
  const raw = pageText.indexOf(phrase);
  if (raw !== -1) return [raw, raw + phrase.length];
  const idx = collapsed().text.indexOf(phrase);
  if (idx === -1 || !phrase.length) return null;
  const { at } = collapsed();
  return [at[idx], at[idx + phrase.length - 1] + 1];
};

/**
 * The `[start, end)` span of the page text that holds the cited passage, or
 * null when the passage cannot be located.
 *
 * The whole context is tried first. A harvested chunk often spans a page
 * break, so when the whole thing is absent we fall back to its longest
 * sentence that IS present — longest because a short sentence ("See below.")
 * localizes nothing and would pick an arbitrary window.
 */
const locateContext = (pageText, rawContext) => {
  const context = plainText(rawContext);
  if (!context) return null;

  let collapsedText = null;
  const collapsed = () => {
    if (collapsedText === null) collapsedText = collapsedPage(pageText);
    return collapsedText;
  };

  const whole = findOnPage(pageText, collapsed, context);
  if (whole !== null) return whole;

  let best = null;
  let bestLength = 0;
  for (const sentence of splitIntoSentences(context)) {
    // A short sentence localizes nothing. "See below." or a recurring header
    // line matches somewhere arbitrary, and if the figure happens to sit in
    // that bogus window the anchor is WORSE than the first-occurrence
    // behavior it replaced — which this pass is not allowed to be.
    if (sentence.length < MIN_ANCHOR_SENTENCE_CHARS) continue;
    if (best !== null && sentence.length <= bestLength) continue;
    const span = findOnPage(pageText, collapsed, sentence);
    if (span !== null) {
      best = span;
      bestLength = sentence.length;
    }
  }
  return best;
};

/**
 * Index of the occurrence of `phrase` in `pageText` that the citation means,
 * or -1 when the phrase is absent from the page.
 *
 * Callers pass text they have already normalized (case, decimal separators);
 * this function does no normalization of its own so the index it returns lines
 * up with the caller's own offsets.
 */
export const chooseAnchoredOccurrence = ({ pageText, phrase, context }) => {
  const hits = allOccurrences(pageText || "", phrase || "");
  if (!hits.length) return -1;

  const window = locateContext(pageText || "", (context || "").trim());
  if (window === null) return hits[0];

  const [start, end] = window;
  const inside = hits.find((idx) => idx >= start && idx + phrase.length <= end);
  // A locatable passage that simply doesn't contain the figure (the model
  // cited a passage for a figure it derived, or the chunk text is a table
  // caption) still gets the pre-existing highlight rather than none.
  return inside === undefined ? hits[0] : inside;
};

/**
 * `textNodes` reordered so the node holding `anchorIdx` comes first, with the
 * rest following in page order.
 *
 * The single-node highlight pass scans nodes in order and takes the first hit.
 * Starting that scan at the anchored node is what makes it pick the cited
 * occurrence; keeping every other node in the list afterwards is what keeps
 * the old first-match result reachable when the anchored node can't be
 * highlighted. A -1 anchor (phrase absent) leaves the order untouched.
 *
 * Offsets are computed exactly the way the cross-node pass builds its own
 * joined string — one space between nodes — so the two agree on what
 * `anchorIdx` points at.
 */
export const orderedFromAnchor = (textNodes, anchorIdx) => {
  if (!(anchorIdx >= 0) || textNodes.length < 2) return textNodes;

  let offset = 0;
  let anchoredNode = -1;
  for (let i = 0; i < textNodes.length; i++) {
    if (i) offset += 1;
    const len = (textNodes[i].textContent || "").length;
    if (anchorIdx >= offset && anchorIdx < offset + len) {
      anchoredNode = i;
      break;
    }
    offset += len;
  }
  if (anchoredNode <= 0) return textNodes;

  return [
    textNodes[anchoredNode],
    ...textNodes.slice(0, anchoredNode),
    ...textNodes.slice(anchoredNode + 1),
  ];
};

/**
 * The hit the citation means, out of `hits` (`{ index, length }` in page
 * order, as `figureHits` produces them), or null when there are none.
 *
 * Same rule as `chooseAnchoredOccurrence` — the hit inside the located
 * passage wins — with one more preference for a passage that locates but
 * does not hold the figure: the nearest hit AFTER it (#12090). A cited
 * table's caption is what locates, and its cells come after it, so "the
 * first hit past the caption" is the cell far more often than "the first hit
 * on the page". That is a heuristic, not a guarantee: a prose passage citing
 * a figure printed in a table ABOVE it now loses to a later hit where the
 * first-hit rule would have found it. Tables cite downward far more often
 * than upward, so the trade is taken. A passage that cannot be located
 * yields the first hit, as before.
 *
 * `pageText` is the caller's normalized page text and `hits` index into it.
 */
export const chooseAnchoredHit = ({ pageText, hits, context }) => {
  if (!hits || !hits.length) return null;

  const window = locateContext(pageText || "", (context || "").trim());
  if (window === null) return hits[0];

  const [start, end] = window;
  const inside = hits.find(
    (hit) => hit.index >= start && hit.index + hit.length <= end
  );
  if (inside !== undefined) return inside;
  const after = hits.find((hit) => hit.index >= end);
  return after === undefined ? hits[0] : after;
};
